import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Info, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { ErrorState, FormField, PageHeader, StatusPill } from '@/components/app';
import {
  AppConfig,
  AppPlatform,
  LocalizedMessage,
  UpdateAppConfigPayload,
  appConfigApi,
} from '../../api/appConfig';
import { getErrorMessage } from '@/lib/errors';
import { compareVersions, isValidVersion } from '@/lib/version';
import { TASHKENT_TZ } from '@/lib/datetime';

type Lang = 'ru' | 'uz' | 'kiril';
const LANGS: Lang[] = ['ru', 'uz', 'kiril'];
const LANG_LABEL: Record<Lang, string> = { ru: 'Русский', uz: 'O\'zbek', kiril: 'Ўзбек (кир.)' };

const EMPTY: LocalizedMessage = { ru: '', uz: '', kiril: '' };
const PLATFORMS: AppPlatform[] = ['ios', 'android'];

type LocalizedField = 'force_update_message' | 'soft_update_message' | 'maintenance_title' | 'maintenance_message';

function platformLabel(p: AppPlatform): string {
  return p === 'ios' ? 'iOS' : 'Android';
}

/** Поля, которые уходят в PATCH, — по ним же считаем «есть несохранённые изменения». */
function toPayload(c: AppConfig): UpdateAppConfigPayload {
  return {
    latest_version: c.latest_version,
    min_required_version: c.min_required_version,
    store_url: c.store_url,
    force_update_message: c.force_update_message,
    soft_update_message: c.soft_update_message,
    maintenance_title: c.maintenance_title,
    maintenance_message: c.maintenance_message,
    maintenance_mode: c.maintenance_mode,
    telegram_login_enabled: !!c.telegram_login_enabled,
  };
}

const isDirty = (draft: AppConfig | null, saved: AppConfig | undefined) =>
  !!draft && !!saved && JSON.stringify(toPayload(draft)) !== JSON.stringify(toPayload(saved));

const formatUpdated = (s: string) =>
  new Date(s).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TASHKENT_TZ,
  });

// Пропсы маршрута (App.tsx) не используются: тема приходит из CSS-переменных.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function AppConfigPage(_props: Record<string, unknown>) {
  const confirm = useConfirm();
  const [configs, setConfigs] = useState<AppConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [platform, setPlatform] = useState<AppPlatform>('ios');
  const [savingPlatform, setSavingPlatform] = useState<AppPlatform | null>(null);
  const [drafts, setDrafts] = useState<Record<AppPlatform, AppConfig | null>>({
    ios: null,
    android: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await appConfigApi.list();
      setConfigs(list);
      const drafts: Record<AppPlatform, AppConfig | null> = { ios: null, android: null };
      for (const c of list) drafts[c.platform] = c;
      setDrafts(drafts);
    } catch (err) {
      setLoadError(getErrorMessage(err, 'Не удалось загрузить конфиг'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateDraft = (platform: AppPlatform, patch: Partial<AppConfig>) => {
    setDrafts(prev => ({
      ...prev,
      [platform]: prev[platform] ? { ...prev[platform]!, ...patch } : prev[platform],
    }));
  };

  const updateLocalized = (platform: AppPlatform, field: LocalizedField, lang: Lang, value: string) => {
    const current = drafts[platform];
    if (!current) return;
    updateDraft(platform, {
      [field]: { ...(current[field] || EMPTY), [lang]: value },
    } as Partial<AppConfig>);
  };

  const resetDraft = (platform: AppPlatform) => {
    const saved = configs.find(c => c.platform === platform) ?? null;
    setDrafts(prev => ({ ...prev, [platform]: saved }));
  };

  const save = async (platform: AppPlatform) => {
    const draft = drafts[platform];
    if (!draft) return;

    // Версии — строго x.y.z: опечатка («12.0») в минимальной версии заблокировала бы приложение.
    for (const [label, value] of [
      ['Последняя версия', draft.latest_version],
      ['Минимально допустимая версия', draft.min_required_version],
    ] as const) {
      if (!isValidVersion(value || '')) {
        toast.error(`${label}: укажите версию в формате 1.2.3`);
        return;
      }
    }

    // Подтверждение при ПОВЫШЕНИИ минимальной версии — это принудительное обновление.
    const prevMin = configs.find(c => c.platform === platform)?.min_required_version ?? '';
    const minCmp = compareVersions(draft.min_required_version, prevMin);
    if (minCmp === null ? draft.min_required_version.trim() !== prevMin.trim() : minCmp > 0) {
      const ok = await confirm({
        title: `Поднять минимальную версию ${platformLabel(platform)} до ${draft.min_required_version.trim()}?`,
        description:
          `Все пользователи ${platformLabel(platform)} с версией ниже ${draft.min_required_version.trim()} ` +
          'увидят блокирующий экран обновления и не смогут пользоваться приложением, пока не обновятся.' +
          (prevMin ? ` Сейчас минимальная версия — ${prevMin}.` : ''),
        confirmLabel: 'Включить обновление',
        destructive: true,
      });
      if (!ok) return;
    }

    // Подтверждение при ВКЛЮЧЕНИИ режима обслуживания — он блокирует приложение всем.
    const wasMaintenance = configs.find(c => c.platform === platform)?.maintenance_mode ?? false;
    if (draft.maintenance_mode && !wasMaintenance) {
      const ok = await confirm({
        title: 'Включить режим обслуживания?',
        description: `Приложение (${platformLabel(platform)}) будет заблокировано для всех пользователей экраном обслуживания.`,
        confirmLabel: 'Включить',
        destructive: true,
      });
      if (!ok) return;
    }

    setSavingPlatform(platform);
    try {
      const updated = await appConfigApi.update(platform, toPayload(draft));
      updateDraft(platform, updated);
      setConfigs(prev => prev.map(c => (c.platform === platform ? updated : c)));
      toast.success(`${platformLabel(platform)}: сохранено`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Не удалось сохранить'));
    } finally {
      setSavingPlatform(null);
    }
  };

  const header = (
    <PageHeader
      title="Конфигурация приложения"
      subtitle="Версии, режим обслуживания и сообщения для мобильных приложений iOS и Android — без релиза."
    />
  );

  if (loading) {
    return (
      <div className="flex flex-col gap-5" aria-busy>
        {header}
        <Skeleton className="h-10 w-48 rounded-xl" />
        <Skeleton className="h-28 w-full rounded-card" />
        <Skeleton className="h-56 w-full rounded-card" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col gap-5">
        {header}
        <Card>
          <ErrorState message={loadError} onRetry={load} />
        </Card>
      </div>
    );
  }

  const draft = drafts[platform];
  const saved = configs.find(c => c.platform === platform);
  const dirty = isDirty(draft, saved);
  const latestOk = !!draft && isValidVersion(draft.latest_version || '');
  const minOk = !!draft && isValidVersion(draft.min_required_version || '');
  const errorCount = (latestOk ? 0 : 1) + (minOk ? 0 : 1);
  const saving = savingPlatform === platform;

  return (
    <div className="flex flex-col gap-5">
      {header}

      <Tabs value={platform} onValueChange={(v) => setPlatform(v as AppPlatform)}>
        <TabsList aria-label="Платформа">
          {PLATFORMS.map(p => (
            <TabsTrigger key={p} value={p}>
              {platformLabel(p)}
              {isDirty(drafts[p], configs.find(c => c.platform === p)) && (
                <span className="size-1.5 rounded-full bg-warn" aria-label="есть несохранённые изменения" />
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {PLATFORMS.map(p => (
          <TabsContent key={p} value={p} className="mt-4">
            {!drafts[p] ? (
              <Card className="p-[18px] text-sm text-text-2">
                Конфиг для {platformLabel(p)} не найден на сервере.
              </Card>
            ) : (
              <PlatformForm
                platform={p}
                draft={drafts[p]!}
                onChange={(patch) => updateDraft(p, patch)}
                onLocalized={(field, lang, value) => updateLocalized(p, field, lang, value)}
              />
            )}
          </TabsContent>
        ))}
      </Tabs>

      {draft && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (!saving) void save(platform);
          }}
          className="sticky bottom-0 z-10 -mx-4 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-surface px-4 py-3 lg:-mx-7 lg:px-7"
        >
          <span role="status" className="flex flex-wrap items-center gap-3 text-sm">
            <b>{platformLabel(platform)}</b>
            <span className="text-[13px] text-text-2">
              {dirty ? 'Есть несохранённые изменения' : 'Изменений нет'} · обновлено{' '}
              <span className="tabular-nums">{formatUpdated(draft.updated_at)}</span>
            </span>
            {errorCount > 0 && (
              <StatusPill size="sm" tone="neg">
                {errorCount === 1 ? '1 ошибка' : `${errorCount} ошибки`} — исправьте перед сохранением
              </StatusPill>
            )}
          </span>
          <span className="flex gap-2">
            <Button type="button" variant="secondary" onClick={() => resetDraft(platform)} disabled={!dirty || saving}>
              Отменить
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="animate-spin" aria-hidden />}
              Сохранить изменения
            </Button>
          </span>
        </form>
      )}
    </div>
  );
}

interface PlatformFormProps {
  platform: AppPlatform;
  draft: AppConfig;
  onChange: (patch: Partial<AppConfig>) => void;
  onLocalized: (field: LocalizedField, lang: Lang, value: string) => void;
}

function PlatformForm({ platform, draft, onChange, onLocalized }: PlatformFormProps) {
  const latestOk = isValidVersion(draft.latest_version || '');
  const minOk = isValidVersion(draft.min_required_version || '');
  const id = (s: string) => `${platform}-${s}`;

  const localized = (field: LocalizedField, multiline: boolean, rows = 2) =>
    LANGS.map(lang => (
      <FormField key={lang} label={LANG_LABEL[lang]} tone="filter">
        {multiline ? (
          <Textarea
            value={draft[field]?.[lang] || ''}
            onChange={e => onLocalized(field, lang, e.target.value)}
            rows={rows}
          />
        ) : (
          <Input value={draft[field]?.[lang] || ''} onChange={e => onLocalized(field, lang, e.target.value)} />
        )}
      </FormField>
    ));

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <ToggleCard
          id={id('maintenance')}
          label="Режим обслуживания"
          description="При включении приложение полностью блокируется и показывает заголовок с сообщением ниже."
          checked={draft.maintenance_mode}
          onCheckedChange={v => onChange({ maintenance_mode: v })}
          warn
        />
        <ToggleCard
          id={id('telegram')}
          label="Вход через Telegram"
          description="Показывает кнопку Telegram на экране входа. Выключите, если бот или Telegram недоступны — кнопка спрячется без обновления приложения."
          checked={!!draft.telegram_login_enabled}
          onCheckedChange={v => onChange({ telegram_login_enabled: v })}
        />
      </div>

      <section aria-labelledby={id('versions')} className="flex flex-col gap-2.5">
        <h2 id={id('versions')} className="m-0 font-display text-[17px] font-bold">Версии</h2>
        <Card className="grid grid-cols-1 gap-4 p-[18px] md:grid-cols-2">
          <FormField label="Последняя версия (в сторе)" error={latestOk ? undefined : 'Формат версии: 1.2.3'}>
            <Input
              value={draft.latest_version}
              onChange={e => onChange({ latest_version: e.target.value })}
              placeholder="1.2.0"
              className="tabular-nums"
            />
          </FormField>
          <FormField
            label="Минимально допустимая"
            error={minOk ? undefined : 'Формат версии: 1.2.3'}
            hint="Ниже этой версии — Force Update (блокирующий экран)."
          >
            <Input
              value={draft.min_required_version}
              onChange={e => onChange({ min_required_version: e.target.value })}
              placeholder="1.0.0"
              className="tabular-nums"
            />
          </FormField>
          <FormField label="Ссылка на стор" className="md:col-span-2">
            <Input
              value={draft.store_url}
              onChange={e => onChange({ store_url: e.target.value })}
              placeholder={
                platform === 'ios'
                  ? 'https://apps.apple.com/app/...'
                  : 'https://play.google.com/store/apps/details?id=...'
              }
            />
          </FormField>
        </Card>
      </section>

      <section aria-labelledby={id('messages')} className="flex flex-col gap-2.5">
        <h2 id={id('messages')} className="m-0 font-display text-[17px] font-bold">Сообщения</h2>
        <Card className="p-[18px]">
          <Tabs defaultValue="force">
            <TabsList variant="underline" aria-label="Тип сообщения">
              <TabsTrigger value="force">Force Update</TabsTrigger>
              <TabsTrigger value="soft">Soft Update</TabsTrigger>
              <TabsTrigger value="maintenance">Обслуживание</TabsTrigger>
            </TabsList>

            <TabsContent value="force" className="mt-4 flex flex-col gap-3">
              <Note>Сообщение на блокирующем экране, когда версия ниже минимально допустимой.</Note>
              {localized('force_update_message', true)}
            </TabsContent>

            <TabsContent value="soft" className="mt-4 flex flex-col gap-3">
              <Note>Сообщение во всплывающем окне — его можно закрыть и продолжить пользоваться приложением.</Note>
              {localized('soft_update_message', true)}
            </TabsContent>

            <TabsContent value="maintenance" className="mt-4 flex flex-col gap-5">
              <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
                <legend className="mb-1 text-sm font-bold">Заголовок</legend>
                {localized('maintenance_title', false)}
              </fieldset>
              <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
                <legend className="mb-1 text-sm font-bold">Сообщение</legend>
                {localized('maintenance_message', true, 3)}
              </fieldset>
            </TabsContent>
          </Tabs>
        </Card>
      </section>
    </div>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="m-0 flex items-start gap-2.5 rounded-xl bg-muted px-3.5 py-3 text-[13px] text-text-2">
      <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
      <span>{children}</span>
    </p>
  );
}

interface ToggleCardProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
  /** Включённое состояние опасно — подсветить предупреждением. */
  warn?: boolean;
}

function ToggleCard({ id, label, description, checked, onCheckedChange, warn }: ToggleCardProps) {
  const active = warn && checked;
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-card border bg-surface px-[18px] py-4 shadow-card transition-colors',
        active ? 'border-warn bg-warn-soft' : 'border-border-soft',
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="flex flex-wrap items-center gap-2">
          <Label htmlFor={id} className="text-sm">{label}</Label>
          {active && <StatusPill size="sm" tone="warn">Включён</StatusPill>}
        </span>
        <p id={`${id}-desc`} className="m-0 text-[13px] text-text-2">{description}</p>
      </div>
      <Switch id={id} aria-describedby={`${id}-desc`} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}
